/**
 * Google Routes API (v2) directions helper.
 * Same approach as the driver app — uses computeRoutes endpoint.
 * Requires "Routes API" enabled in Google Cloud Console.
 */

const ROUTES_ENDPOINT = 'https://routes.googleapis.com/directions/v2:computeRoutes';

/**
 * Decode a Google encoded polyline string into an array of { lat, lng }.
 * Mirrors the driver app's decodeEncodedPolyline but outputs { lat, lng }
 * for Google Maps web (instead of { latitude, longitude } used in React Native).
 */
function decodeEncodedPolyline(encoded) {
  const points = [];
  let index = 0;
  const len = encoded.length;
  let lat = 0;
  let lng = 0;

  while (index < len) {
    let b;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lat += dlat;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lng += dlng;

    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
}

/**
 * Fetch a driving route using Google Routes API v2 — computeRoutes.
 * Matches the driver app's fetchRouteFromRoutesApi logic.
 *
 * @param {{ lat: number, lng: number }} origin
 * @param {{ lat: number, lng: number }} destination
 * @returns {Promise<{ coordinates: Array<{lat,lng}>, distance: number, duration: number }>}
 */
export async function computeRoute(origin, destination) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    console.warn('Google Maps API key not configured');
    return { coordinates: [], distance: 0, duration: 0 };
  }

  const body = {
    origin: {
      location: {
        latLng: {
          latitude: origin.lat,
          longitude: origin.lng,
        },
      },
    },
    destination: {
      location: {
        latLng: {
          latitude: destination.lat,
          longitude: destination.lng,
        },
      },
    },
    travelMode: 'DRIVE',
    polylineQuality: 'OVERVIEW',
  };

  const res = await fetch(ROUTES_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey,
      'X-Goog-FieldMask': 'routes.duration,routes.distanceMeters,routes.polyline.encodedPolyline',
    },
    body: JSON.stringify(body),
  });

  const data = await res.json();

  if (!res.ok) {
    const errMsg = data?.error?.message || data?.message || `HTTP ${res.status}`;
    console.warn('Routes API error:', errMsg);
    return { coordinates: [], distance: 0, duration: 0 };
  }

  const route = data?.routes?.[0];
  if (!route) {
    console.warn('Routes API: no route in response');
    return { coordinates: [], distance: 0, duration: 0 };
  }

  const encodedPolyline = route?.polyline?.encodedPolyline;
  if (!encodedPolyline) {
    console.warn('Routes API: no polyline in route');
    return { coordinates: [], distance: 0, duration: 0 };
  }

  const coordinates = decodeEncodedPolyline(encodedPolyline);
  const distance = route.distanceMeters ?? 0;

  let duration = 0;
  if (route.duration) {
    const d = String(route.duration).replace(/s$/, '');
    duration = parseInt(d, 10) || 0;
  }

  return { coordinates, distance, duration };
}
